import { Fragment, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useFormik } from 'formik'
import { TableContext } from 'Context/Table'
import CustomShippingItemAction from './Components/CustomShppingItemAction'
import { ShippingListViewContext } from '../List/Context'
import { ShippingFormModalContext } from './Context'
import { useShippingRequestStore, INITIAL_STATE } from 'Store/ShippingRequest'
import { useShippingStore } from 'Store/Shipping'
import { useSaleOrderStore } from 'Store/SaleOrder'
import { useAuthStore } from 'Store/Auth'
import * as Yup from 'yup'
import moment from 'moment'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import PileInput from './Components/PileInput'
import formatNumberCommas from 'Utils/formatNumberCommas'
import RolePermissionConfigs from 'Configuration/RolePermission'

const VALIDATION_SCHEMA = Yup.object().shape({
  items: Yup.array().of(
    Yup.object().shape({
      shippingQuantity: Yup.string(),
    })
  ),
})

const ViewModel = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const roleId = useAuthStore(state => state.profile?.roleId ?? 0, shallow)
  // get saleOrderId from query string
  const paramSaleOrderId = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('saleOrderId')
  }, [location.search])
  const paramProjectId = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('projectId')
  }, [location.search])
  const isCreate = location.pathname.includes('create')
  const isView = location.search.includes('view')
  const {
    // currentSorting,
    updateLoading,
    updateError,
    updatePagination,
    updateSorting,
    resetState: resetTableState,
  } = useContext(TableContext)
  const { isOpenShippingModal, toggleShippingModal } = useContext(ShippingListViewContext)
  const { formType } = useContext(ShippingFormModalContext)
  const { selected, getOne } = useShippingRequestStore(
    state => ({
      selected: state.selected,
      getAll: state.getAll,
      getOne: state.getOne,
    }),
    shallow
  )
  const { create, update } = useShippingRequestStore(state => ({
    create: state.create,
    update: state.edit,
  }))
  const { deliverShipping, deleteShipping, uploadFile, downloadFile } = useShippingStore(state => ({
    deliverShipping: state.deliverShipping,
    deleteShipping: state.delete,
    uploadFile: state.uploadAttachFile,
    downloadFile: state.downloadAttachFile,
  }))

  const [staticShippingRequest, setStaticShippingRequest] = useState<any>({
    saleOrderCode: '',
    division: '',
    customerTitle: '',
    status: '',
    saleOrderId: '',
    requestMessage: '',
  })
  const [shippingOrders, setShippingOrders] = useState<any[]>([])
  const { getSaleOrder, getSaleOrderItems } = useSaleOrderStore(
    state => ({
      getSaleOrder: state.getOne,
      saleOrderItems: state.items,
      setSetSaleOrderItems: state.setItems,
      getSaleOrderItems: state.getItems,
    }),
    shallow
  )

  const setupTable = () => {
    updatePagination(true)
    updateSorting('itemCode', true)
  }

  const submit = async (values: any) => {
    if (isCreate) {
      if (values.pilePerRound && isNaN(values.pilePerRound)) {
        values.pilePerRound = 0
      }
      const body: { [key: string]: any } = {
        saleOrderId: staticShippingRequest.saleOrderId,
        requestMessage: formik.values.requestMessage,
        shippingItems: values.shippingItems.filter((s: any) => Number(s.pileQuantity) > 0),
      }

      const { success, data } = await create(body)
      if (!success) {
        toast.error(data)
        return
      } else {
        toast.success('สร้างคำขอการขนส่งสำเร็จ')
        // redirect back
        if (paramProjectId) {
          navigate(`/project/${paramProjectId}/shipping-request`)
        } else {
          navigate('/shipping-request')
        }
      }
    } else if (!isCreate) {
      const body: { [key: string]: any } = {}

      Object.keys(values).forEach(key => {
        if (key === 'shippingItems') {
          const newShippingItems = values[key].filter((item: any) => item.isDelete || item.isEdit)
          if (newShippingItems.length === 0) return
          body[key] = newShippingItems
        }
      })

      if (Object.keys(body).length === 0) {
        toast.error('ไม่มีการเปลี่ยนแปลงใดข้อมูล')
        return
      }

      const { data, success } = await update(id ? parseInt(id) : 0, body)
      if (!success) {
        toast.error(data)
        return
      } else {
        toast.success('แก้ไขคำขอการขนส่งสำเร็จ')
        toggleShippingModal(false)
        await getOne(id ? parseInt(id) : 0)
      }
    }
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: INITIAL_STATE,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: async (values, { resetForm }) => {
      await submit(values)
      resetForm()
    },
  })
  const hasItemInvalid = useMemo(() => {
    if (
      formik.values.shippingItems.some(s => Number(s?.shippingQuantity) > s?.availableForShipment)
    ) {
      return true
    }

    return false
  }, [formik.values.shippingItems])

  const fetchDataForUpdate = async () => {
    updateLoading(true)

    if (id) {
      const { data, success } = await getOne(id ? parseInt(id) : 0)
      if (!success) {
        toast.error(data)
        toggleShippingModal(false)
        updateError(true)
        updateLoading(false)
        return
      } else {
        setShippingOrders(data.shippingOrders)
        formik.setValues({
          shippingItems: data.items.map((item: any, index: number) => ({
            index,
            id: item.id,
            pileQuantity: item.pileQuantity,
            pileLength: item.pileLength,
            groupLetter: item.groupLetter,
            itemTitle: item.itemTitle,
          })),
          requestMessage: data.requestMessage,
        })
        setStaticShippingRequest({
          saleOrderId: data.saleOrderId,
          saleOrderCode: data.saleOrderCode,
          division: data.division,
          customerTitle: data.customerTitle,
          requestMessage: data.requestMessage,
          status: data.status,
        })
        updateLoading(false)
        updateError(false)
      }
    }
  }
  const fetchDataForCreate = async () => {
    if (paramSaleOrderId) {
      getSaleOrder(parseInt(paramSaleOrderId))
        .then(({ success, data }) => {
          let saleOrder = data.saleOrder
          if (!success) {
            toggleShippingModal(false)
            updateError(true)
            updateLoading(false)
            return
          } else {
            setStaticShippingRequest({
              saleOrderId: saleOrder.id,
              saleOrderCode: saleOrder.code,
              division: saleOrder.division,
              customerTitle: saleOrder.customerTitle,
              status: 'draft',
            })

            getSaleOrderItems(saleOrder.id).then(({ success, data: dataItems }) => {
              formik.setValues({
                shippingItems: dataItems.map((item: any, index: number) => ({
                  ...item,
                  index,
                })),
                requestMessage: '',
              })
              updateError(false)
              updateLoading(false)
            })
          }
        })
        .catch(err => {
          console.log(err)
        })
    }
  }

  const handleClose = () => {
    toggleShippingModal(false)
    navigate('/shipping-request')
    formik.resetForm()
  }

  const handleDeleteShipping = async (deleteId: number) => {
    const result = await Swal.fire({
      title: 'ลบคำขอการขนส่ง?',
      text: 'คุณต้องการที่จะลบคำขอการขนส่งนี้หรือไม่?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    })

    if (result.isConfirmed) {
      const { success, data } = await deleteShipping(deleteId)
      if (!success) {
        toast.error(data)
        return
      } else {
        toast.success('ลบคำขอการขนส่งสำเร็จ')
        fetchDataForUpdate()
      }
    }
  }

  const onChangeSaleOrderItem = (idx: number, field: string, value: any) => {
    const isNumber = !isNaN(value)
    if (!isNumber) return
    const newSaleOrderItems = [...formik.values.shippingItems]
    const index = newSaleOrderItems.findIndex((item: any) => item.index === idx)

    newSaleOrderItems[index][field] = value
    newSaleOrderItems[index]['isEdit'] = true

    formik.setFieldValue('shippingItems', newSaleOrderItems)
  }

  const isValidDateForm = (currentDate: any, selectedDate: any): boolean => {
    return moment(currentDate).isSameOrBefore(selectedDate)
  }

  const viewShippingItemDetail = (detailId: number) => {
    navigate(`/shipping-request/${id}/shipping-order/${detailId}`)
  }

  const deliveryShippingItem = (detailId: number) => {
    Swal.fire({
      title: 'ลบรายการที่จะขนส่ง',
      text: 'คุณต้องการที่จะขนส่งรายการนี้หรือไม่?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      customClass: {
        confirmButton: 'btn btn-success me-3',
        cancelButton: 'btn btn-danger',
      },
    }).then(async result => {
      if (result.isConfirmed) {
        const { success, data } = await deliverShipping(detailId)
        if (!success) {
          toast.error(data)
          return
        } else {
          toast.success('Delivery shipping successfully')
          fetchDataForUpdate()
        }
      }
    })
  }

  const scanShippingItemQrCode = (detailId: number) => {
    navigate(`/shipping-request/${id}/shipping-order/${detailId}/scan`)
  }

  const printShippingItem = (detailId: number) => {
    window.open(`/print?shippingId=${detailId}`, '_blank')
  }

  const onDownloadFile = (id: number) => {
    const shippingOrder = selected.shippingOrders.find((so: any) => so.id === id)
    if (!shippingOrder || !shippingOrder.fileName) {
      toast.error('ไม่พบไฟล์แนบ')
      return
    }
    downloadFile(id).then(({ success, data }) => {
      if (!success) {
        toast.error(data)
        return
      }

      try {
        let href: string | null = null

        if (data instanceof Blob) {
          href = URL.createObjectURL(data)
        } else if (typeof data === 'string') {
          href = data
        } else {
          toast.error('รูปแบบไฟล์ไม่ถูกต้อง')
          return
        }

        const link = document.createElement('a')
        link.href = href
        link.download = shippingOrder.fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        if (data instanceof Blob && href) {
          // Give the browser a moment before revoking
          setTimeout(() => URL.revokeObjectURL(href as string), 1000)
        }
      } catch (e) {
        toast.error('ไม่สามารถดาวน์โหลดไฟล์ได้')
      }
    })
  }

  const onUploadFile = (id: number) => {
    Swal.fire({
      title: 'อัพโหลดไฟล์แนบใบขนส่ง',
      text: 'กรุณาเลือกไฟล์แนบใบขนส่ง (PDF, JPG, PNG)',
      input: 'file',
      inputAttributes: {
        accept: '.pdf,.jpg,.png',
        'aria-label': 'Upload your file',
      },
      showCancelButton: true,
      confirmButtonText: 'อัพโหลด',
      cancelButtonText: 'ยกเลิก',
      customClass: {
        confirmButton: 'btn btn-success me-3',
        cancelButton: 'btn btn-danger',
      },
      preConfirm: (fileInput: any) => {
        if (!fileInput) {
          Swal.showValidationMessage('กรุณาเลือกไฟล์')
        }
        return fileInput
      },
    }).then(async result => {
      if (result.isConfirmed && result.value) {
        const selectedFile = result.value as File
        const { success, data } = await uploadFile(id, selectedFile)
        if (!success) {
          toast.error(data)
          return
        } else {
          toast.success('อัพโหลดไฟล์แนบสำเร็จ')
          fetchDataForUpdate()
        }
      }
    })
  }

  const modalsRoot = document.getElementById('root-modals') || document.body

  const CREATE_PILE_ITEMS_CONFIGS: any[] = [
    {
      Header: 'ชื่อกลุ่มเสา',
      accessor: 'groupLetter',
      Cell: ({ value }: any) => {
        return (
          <div className=''>
            <span className='text-gray-800'>{value}</span>
          </div>
        )
      },
    },
    {
      Header: 'รายการเสา',
      accessor: 'itemTitle',
      Cell: ({ value }: any) => {
        return value.map((item: any, index: number) => <div key={index}>{item}</div>)
      },
    },
    {
      Header: 'ความยาวเสา',
      accessor: 'actualQuantity',
      width: 80,
      minWidth: 80,
      headerRight: true,
      Cell: ({ value }: any) => {
        return (
          <div className='d-flex justify-content-end'>
            <span className='text-gray-800'>{value}</span>
          </div>
        )
      },
    },
    {
      Header: 'จำนวนทั้งหมด',
      headerRight: true,
      width: 80,
      minWidth: 80,
      accessor: 'quantity',
      Cell: ({ value }: any) => {
        return (
          <div className='d-flex justify-content-end'>
            <span className='text-gray-800 text-end'>{formatNumberCommas(value ?? 0)}</span>
          </div>
        )
      },
    },
    {
      Header: 'จำนวนที่ต้องการ',
      headerRight: true,
      accessor: 'pileQuantity',
      field: 'pileQuantity',
      width: 100,
      onChange: onChangeSaleOrderItem,
      isView: isView,
      Cell: PileInput,
    },
  ]
  const UPDATE_PILE_ITEMS_CONFIGS: any[] = [
    {
      Header: 'กลุ่มเสา',
      accessor: 'groupLetter',
      Cell: ({ row, value }: any) => {
        return (
          <div className='d-flex'>
            <span className='text-gray-800'>{value}</span>
          </div>
        )
      },
    },
    {
      Header: 'รายการเสา',
      accessor: 'itemTitle',
      Cell: ({ value }: any) => {
        return value.map((item: any, index: number) => <div key={index}>{item}</div>)
      },
    },
    {
      Header: 'ความยาวเสา',
      accessor: 'pileLength',
      width: 80,
      minWidth: 80,
      headerRight: true,
      Cell: ({ row, value }: any) => {
        return (
          <div className='d-flex justify-content-end'>
            <span className='text-gray-800'>{value}</span>
          </div>
        )
      },
    },
    {
      Header: 'จำนวนที่ร้องขอ',
      headerRight: true,
      accessor: 'pileQuantity',
      Cell: ({ value }: any) => {
        return (
          <div className='d-flex justify-content-end'>
            <span className='text-gray-800 text-end'>{formatNumberCommas(value ?? 0)}</span>
          </div>
        )
      },
    },
    // {
    //   Header: 'ส่งแล้ว',
    //   headerRight: true,
    //   accessor: 'shippedQuantity',
    //   Cell: ({ value }: any) => {
    //     return (
    //       <div className='d-flex justify-content-end'>
    //         <span className='text-gray-800 text-end'>{formatNumberCommas(value ?? 0)}</span>
    //       </div>
    //     )
    //   },
    // },
  ]
  const SHIPPING_ORDER_LIST_CONFIGS: any[] = [
    {
      Header: 'เลขที่ใบขนส่ง',
      accessor: 'transferDocumentNo',
      width: 200,
      minWidth: 200,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'วันที่ทำรายการ',
      accessor: 'createdAt',
      width: 80,
      minWidth: 80,
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800'>
            {value ? moment(value).format('DD/MM/YYYY HH:mm') : '-'}
          </span>
        )
      },
    },
    {
      Header: 'วันที่ส่งของ',
      accessor: 'startShippingAt',
      width: 80,
      minWidth: 80,
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800'>
            {value ? moment(value).format('DD/MM/YYYY HH:mm') : '-'}
          </span>
        )
      },
    },
    {
      Header: 'สถานะ',
      accessor: 'jobStatus',
      width: 80,
      minWidth: 80,
      Cell: ({ value }: any) => (
        <span
          className='badge bg-primary'
          style={{
            textTransform: 'capitalize',
          }}>
          {value ?? '-'}
        </span>
      ),
    },
    {
      Header: 'แนบไฟล์หลักฐาน',
      accessor: 'hasAttachment',
      width: 80,
      minWidth: 80,
      Cell: ({ row }: any) => {
        const hasFile = row.original.fileName && row.original.path

        return hasFile ? (
          <span className='badge badge-success'>อัพโหลดแล้ว</span>
        ) : (
          <span className='badge badge-danger'>ยังไม่ได้อัพโหลด</span>
        )
      },
    },
    // {
    //   Header: '',
    //   accessor: 'shipping',
    //   width: 80,
    //   minWidth: 80,
    //   Cell: ({ value, row }: any) => (
    //     <>
    //       {row.original?.jobStatus?.toLowerCase() === 'pending' && (
    //         <button
    //           type='button'
    //           className='btn btn-sm btn-success me-4 fw-bold'
    //           onClick={() => deliveryShippingItem(row.original.id)}>
    //           ขนส่ง
    //         </button>
    //       )}
    //     </>
    //   ),
    // },
    {
      Header: '',
      accessor: 'action',
      width: 40,
      minWidth: 40,
      Cell: ({ row }: any) => {
        const hasFile = row.original.fileName && row.original.path

        return (
          <Fragment>
            <div className='d-flex justify-content-end align-items-center'>
              {![8, 11, 21].includes(roleId) &&
                row.original?.jobStatus?.toLowerCase() === 'pending' && (
                  <button
                    type='button'
                    className='btn btn-sm btn-success me-4 fw-bold'
                    onClick={() => deliveryShippingItem(row.original.id)}>
                    ขนส่ง
                  </button>
                )}
              <CustomShippingItemAction
                id={row.original.id}
                hideScan={
                  row.original.jobStatus.toLowerCase() !== 'pending' || [8, 11].includes(roleId)
                }
                hidePrintLabel={
                  !RolePermissionConfigs.print.includes(roleId) || [8, 11].includes(roleId)
                }
                onClickView={() => viewShippingItemDetail(row.original.id)}
                onClickScanQrCode={() => scanShippingItemQrCode(row.original.id)}
                onClickPrintLabel={() => printShippingItem(row.original.id)}
                onClickDelete={() => {
                  handleDeleteShipping(row.original.id)
                }}
                hideDownloadFile={!hasFile}
                hideDelete={[8, 11, 21].includes(roleId)}
                onDownloadFile={() => onDownloadFile(row.original.id)}
                onUploadFile={() => onUploadFile(row.original.id)}
              />
            </div>
          </Fragment>
        )
      },
      disableSortBy: true,
    },
  ]
  useEffect(() => {
    setupTable()

    return () => {
      resetTableState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isCreate) {
      fetchDataForCreate()
    } else {
      fetchDataForUpdate()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isCreate, isView, isOpenShippingModal])

  return {
    roleId,
    isCreate,
    isView,
    modalsRoot,
    isOpenShippingModal,
    formType,
    handleClose,
    staticShippingRequest,
    paramProjectId,
    formik,
    shippingRequest: selected,
    shippingOrders,
    isValidDateForm,
    // saleOrder: saleOrder,
    SHIPPING_ORDER_LIST_CONFIGS,
    PILE_ITEMS_CONFIGS: isCreate
      ? CREATE_PILE_ITEMS_CONFIGS.filter(i => {
          if (isView && i.accessor === 'shippingQuantity') return false
          else return true
        })
      : UPDATE_PILE_ITEMS_CONFIGS.filter(i => {
          if (isView && i.accessor === 'shippingQuantity') return false
          else return true
        }),
    hasItemInvalid,
  }
}

export default ViewModel
